/** QuickHouse — Maillage 3D d'un étage : murs découpés en boîtes + dalle + sols des pièces */
import { useMemo } from 'react';
import * as THREE from 'three';
import { wallAngle, wallsBounds, wallLength, pointOnWall, distPointToSegment, projectU } from '../../shared/geometry.js';
import { wallToBoxes, effectiveOpeningsForWall } from '../../shared/plan3d.js';
import polygonClipping from 'polygon-clipping';
import { useEffect } from 'react';
import { roomRings, pointInRoom } from '../../shared/rooms.js';
import { stairToBoxes, stairFootprint } from '../../shared/stairs.js';
import { wallHeights } from '../../shared/schema.js';
import { roomHue } from '../editor2d/render2d.js';
import { useProjectStore } from '../store/useProjectStore.js';
import { wallBoxMaterials, surfaceMaterial, glassMaterial, beamMaterial } from './textures.js';
import { beamBox } from '../../shared/beams.js';
import OpeningMesh from './OpeningMesh.jsx';
import ItemMesh from './ItemMesh.jsx';

/** Libère les matériaux (et leurs textures clonées) créés par un useMemo */
function disposeMaterials(list) {
  for (const m of list) {
    if (m.map) m.map.dispose();
    m.dispose();
  }
}

const WALL_COLOR = '#e8e4da';
const SLAB_COLOR = '#8a8f99';
const STAIR_COLOR = '#b08a5a';
const SLAB_MATERIAL_COLORS = { concrete: '#8a8f99', wood: '#a8845c', steel: '#7d838d', other: '#757a85' };
export const ROOF_MATERIAL_COLORS = { slate: '#47505e', tile: '#b3593a', flat: '#9aa0a8', green: '#5d8b4a' };
const SLOPE_DIRS = { N: { x: 0, y: -1 }, S: { x: 0, y: 1 }, E: { x: 1, y: 0 }, W: { x: -1, y: 0 } };

/** MultiPolygon (format polygon-clipping) d'une pièce/dalle */
function roomMulti(room) {
  return roomRings(room).map(({ outer, holes }) => [
    outer.map((p) => [p.x, p.y]),
    ...holes.map((h) => h.map((p) => [p.x, p.y])),
  ]);
}

function multiToShapes(multi) {
  const shapes = [];
  for (const poly of multi) {
    if (!poly[0]?.length) continue;
    const shape = new THREE.Shape(poly[0].map(([x, y]) => new THREE.Vector2(x / 100, y / 100)));
    for (const ring of poly.slice(1)) {
      shape.holes.push(new THREE.Path(ring.map(([x, y]) => new THREE.Vector2(x / 100, y / 100))));
    }
    shapes.push(shape);
  }
  return shapes;
}

export default function FloorMesh({ floor, elevation, slabThickness, rise, stairsBelow = [], belowFloor = null, bottomFill = 0 }) {
  const wallsBelow = (belowFloor?.walls ?? []).filter((w) => !w.hidden);
  // Base d'un élément : le dessus de la dalle ancêtre la plus proche (hiérarchie parent/enfant)
  const baseOffsetOf = useMemo(() => {
    const byId = new Map();
    for (const list of [floor.walls, floor.openings, floor.rooms, floor.stairs ?? []]) {
      for (const it of list) byId.set(it.id, it);
    }
    return (item) => {
      let cur = item.parentId;
      for (let i = 0; i < 50 && cur; i++) {
        const p = byId.get(cur);
        if (!p) break;
        if (p.kind === 'slab') return (p.elevation ?? 0) + (p.thickness ?? slabThickness);
        cur = p.parentId;
      }
      return 0;
    };
  }, [floor, slabThickness]);

  const textures = useProjectStore((s) => s.textures);
  const itemCatalog = useProjectStore((s) => s.catalog);

  const walls = useMemo(() => {
    const visibleOpenings = floor.openings.filter((o) => !o.hidden);
    // Côté EXTÉRIEUR d'un mur (+1 = face locale +z, −1 = face −z) : le côté qui ne donne
    // pas dans une pièce. Sert à poser les volets dehors quel que soit le sens de tracé.
    const realRooms = floor.rooms.filter((r) => (r.kind ?? 'room') === 'room' && r.blocks?.length && !r.hidden);
    const exteriorSideOf = (wall) => {
      const len = wallLength(wall);
      if (!len || !realRooms.length) return 1;
      const mid = { x: (wall.a.x + wall.b.x) / 2, y: (wall.a.y + wall.b.y) / 2 };
      const nx = -(wall.b.y - wall.a.y) / len; // normale plan du côté local +z
      const ny = (wall.b.x - wall.a.x) / len;
      const d = wall.thickness / 2 + 15;
      const inPlus = realRooms.some((r) => pointInRoom(r, { x: mid.x + nx * d, y: mid.y + ny * d }));
      const inMinus = realRooms.some((r) => pointInRoom(r, { x: mid.x - nx * d, y: mid.y - ny * d }));
      if (inPlus && !inMinus) return -1; // pièce côté +z → extérieur en −z
      return 1;
    };
    return floor.walls.filter((w) => !w.hidden).map((wall) => {
      const { hA, hB } = wallHeights(wall, floor.height);
      const base = baseOffsetOf(wall);
      const len = wallLength(wall);
      const effective = effectiveOpeningsForWall(wall, floor.walls, visibleOpenings);
      const boxes = wallToBoxes(wall, effective, hA, hB);
      // Étage supérieur : PLINTHE comblant la bande de dalle sous le mur — là où il y a
      // une DALLE en dessous OU un MUR du niveau inférieur (mur sur mur). Pas au-delà :
      // pas de bandeau flottant devant les fenêtres des murs en retrait.
      if (bottomFill > 0 && base < 50) {
        const baseSlabs = floor.rooms.filter((r) => r.kind === 'slab' && !r.hidden
          && (r.elevation ?? 0) < 50 && r.blocks?.length);
        let spans;
        if (!baseSlabs.length && !wallsBelow.length) {
          spans = [[0, len]]; // dalle automatique seule : elle couvre l'emprise des murs
        } else if (!baseSlabs.length) {
          spans = [[0, len]]; // dalle automatique : pleine couverture (comportement historique)
        } else {
          // Échantillonnage le long de l'axe : couvert si dans une dalle (axe ou ±¼ épaisseur)
          // ou à l'aplomb d'un mur du niveau inférieur.
          const nx = len > 0 ? -(wall.b.y - wall.a.y) / len : 0;
          const ny = len > 0 ? (wall.b.x - wall.a.x) / len : 0;
          const q = wall.thickness / 4;
          const covered = (u) => {
            const p = pointOnWall(wall, u);
            if (baseSlabs.some((s) => pointInRoom(s, p)
              || pointInRoom(s, { x: p.x + nx * q, y: p.y + ny * q })
              || pointInRoom(s, { x: p.x - nx * q, y: p.y - ny * q }))) return true;
            return wallsBelow.some((wb) => distPointToSegment(p, wb.a, wb.b)
              <= (wb.thickness + wall.thickness) / 2 + 2);
          };
          spans = [];
          const step = 10;
          let start = null;
          for (let u = 0; u <= len + step; u += step) {
            const inside = u <= len && covered(Math.min(u, len));
            if (inside && start === null) start = Math.max(0, u - step / 2);
            if (!inside && start !== null) {
              spans.push([start, Math.min(u, len)]);
              start = null;
            }
          }
          spans = spans.filter(([a, b]) => b - a > 5);
        }
        // Fenêtres du niveau inférieur qui montent dans la zone de plinthe :
        // la plinthe est CREUSÉE au droit de la fenêtre (elle ne reprend qu'au-dessus du linteau).
        const elevDelta = (belowFloor?.height ?? 0) + slabThickness;
        const lowerCuts = [];
        if (belowFloor) {
          // Surélévation des murs du dessous posés sur une dalle (parentId → dalle)
          const belowById = new Map();
          for (const list of [belowFloor.walls, belowFloor.openings, belowFloor.rooms, belowFloor.stairs ?? []]) {
            for (const it of list) belowById.set(it.id, it);
          }
          const belowBase = (wb) => {
            let cur = wb.parentId;
            for (let i = 0; i < 50 && cur; i++) {
              const p = belowById.get(cur);
              if (!p) break;
              if (p.kind === 'slab') return (p.elevation ?? 0) + (p.thickness ?? slabThickness);
              cur = p.parentId;
            }
            return 0;
          };
          for (const wb of wallsBelow) {
            const wbBase = belowBase(wb);
            for (const ob of belowFloor.openings) {
              if (ob.hidden || ob.wallId !== wb.id) continue;
              // Sommet de l'ouverture (surélévation du mur incluse), relatif à la base de CET étage
              const topRel = wbBase + ob.sillHeight + ob.height + Math.max(0, ob.arch ?? 0) - elevDelta;
              if (topRel <= -bottomFill + 0.5) continue; // n'atteint pas la plinthe
              const c = pointOnWall(wb, ob.offset);
              if (distPointToSegment(c, wall.a, wall.b) > (wb.thickness + wall.thickness) / 2 + 2) continue;
              const u = projectU(wall, c);
              lowerCuts.push({
                u0: u - ob.width / 2,
                u1: u + ob.width / 2,
                bottom: Math.min(topRel - base, 0), // bas de la plinthe restante (coords locales, plafond = 0)
              });
            }
          }
        }
        const pushFull = (a, b) => {
          if (b - a > 2) boxes.push({ u: a, w: b - a, y0: -bottomFill - base, h: bottomFill + base });
        };
        for (const [s0, s1] of spans) {
          const cuts = lowerCuts
            .filter((c) => c.u1 > s0 + 0.5 && c.u0 < s1 - 0.5)
            .sort((p, q) => p.u0 - q.u0);
          let cursor = s0;
          for (const c of cuts) {
            pushFull(cursor, Math.min(Math.max(cursor, c.u0), s1));
            const a = Math.max(s0, c.u0);
            const b = Math.min(s1, c.u1);
            const hRest = -c.bottom; // hauteur de plinthe restante au-dessus de la fenêtre
            if (hRest > 1 && b - a > 2) {
              boxes.push({ u: a, w: b - a, y0: c.bottom, h: hRest });
            }
            cursor = Math.max(cursor, b);
          }
          pushFull(cursor, s1);
        }
      }
      // Finitions par face : +z = extérieur, −z = intérieur. Textures ancrées au monde :
      // paramètre le long de la droite porteuse + altitude → motif continu partout.
      const dirX = len > 0 ? (wall.b.x - wall.a.x) / len : 1;
      const dirY = len > 0 ? (wall.b.y - wall.a.y) / len : 0;
      const wallParam = (wall.a.x * dirX + wall.a.y * dirY) / 100; // m, continu entre murs alignés
      const withMats = boxes.map((b) => ({
        ...b,
        mats: wallBoxMaterials(
          wall.finishOut, wall.finishIn, textures, b.w / 100, b.h / 100, WALL_COLOR,
          wallParam + b.u / 100,
          (elevation + base + b.y0) / 100,
        ),
      }));
      return { wall, angle: wallAngle(wall), boxes: withMats, base, exteriorSide: exteriorSideOf(wall) };
    });
  }, [floor, baseOffsetOf, bottomFill, textures, belowFloor, slabThickness, elevation]);

  // Libération des matériaux à chaque reconstruction
  useEffect(() => () => {
    for (const w of walls) for (const b of w.boxes) disposeMaterials(b.mats);
  }, [walls]);

  // Trémies : empreintes des escaliers du niveau inférieur, à découper dans les dalles basses
  const stairCuts = useMemo(
    () => stairsBelow.flatMap((s) => stairFootprint(s)).map((ring) => [ring.map((p) => [p.x, p.y])]),
    [stairsBelow],
  );

  // Dalles dessinées (kind 'slab') : extrudées à leur élévation (mezzanine si > 0)
  const drawnSlabs = useMemo(() => {
    const out = [];
    for (const room of floor.rooms) {
      if (room.kind !== 'slab' || !room.blocks?.length || room.hidden) continue;
      let multi = roomMulti(room);
      // Trémie : les escaliers d'en dessous percent les dalles basses de cet étage
      if ((room.elevation ?? 0) < 50 && stairCuts.length) {
        try { multi = polygonClipping.difference(multi, ...stairCuts); } catch { /* dégradé : pas de découpe */ }
      }
      for (const shape of multiToShapes(multi)) {
        out.push({
          key: `${room.id}_${out.length}`,
          geometry: new THREE.ExtrudeGeometry(shape, { depth: (room.thickness ?? slabThickness) / 100, bevelEnabled: false }),
          color: SLAB_MATERIAL_COLORS[room.material] ?? SLAB_COLOR,
          elevation: room.elevation ?? 0,
          thickness: room.thickness ?? slabThickness,
        });
      }
    }
    return out;
  }, [floor, slabThickness, stairCuts]);

  // Toits (kind 'roof') : surface inclinée selon la pente et la direction de descente
  const roofs = useMemo(() => {
    const out = [];
    for (const room of floor.rooms) {
      if (room.kind !== 'roof' || !room.blocks?.length || room.hidden) continue;
      const t = Math.tan(((room.slope ?? 0) * Math.PI) / 180);
      const dir = SLOPE_DIRS[room.slopeDir ?? 'S'];
      for (const shape of multiToShapes(roomMulti(room))) {
        const geo = new THREE.ShapeGeometry(shape);
        const pos = geo.attributes.position;
        let dMax = -Infinity;
        for (let i = 0; i < pos.count; i++) {
          dMax = Math.max(dMax, pos.getX(i) * dir.x + pos.getY(i) * dir.y);
        }
        for (let i = 0; i < pos.count; i++) {
          const d = pos.getX(i) * dir.x + pos.getY(i) * dir.y;
          pos.setZ(i, -t * (dMax - d)); // plus haut à l'opposé de la descente
        }
        geo.computeVertexNormals();
        // Trame de couverture : surcharge `finish` sinon motif du matériau
        const finish = room.finish
          ?? ({ slate: 'slate-roof', tile: 'tile-roof', flat: 'gravel', green: 'green' })[room.material]
          ?? null;
        out.push({
          key: `${room.id}_roof_${out.length}`,
          geometry: geo,
          material: surfaceMaterial(finish, textures,
            ROOF_MATERIAL_COLORS[room.material] ?? '#9aa0a8', { side: THREE.DoubleSide }),
          base: (room.elevation && room.elevation > 0) ? room.elevation : floor.height,
        });
      }
    }
    return out;
  }, [floor, textures]);

  useEffect(() => () => disposeMaterials(roofs.map((r) => r.material)), [roofs]);

  // Dalle automatique uniquement si aucune dalle basse n'est dessinée ;
  // découpée elle aussi par les trémies (via une forme) si nécessaire.
  const hasDrawnBaseSlab = drawnSlabs.length > 0 && floor.rooms.some((r) => r.kind === 'slab' && !r.hidden && (r.elevation ?? 0) < 50);
  const autoSlab = useMemo(() => {
    if (hasDrawnBaseSlab) return null;
    const raw = wallsBounds(floor.walls);
    if (!raw) return null;
    // Léger retrait pour éviter la coplanarité avec le nu des murs (scintillement)
    const b = { minX: raw.minX + 1, minY: raw.minY + 1, maxX: raw.maxX - 1, maxY: raw.maxY - 1 };
    if (!stairCuts.length) {
      return {
        box: {
          cx: (b.minX + b.maxX) / 200,
          cz: (b.minY + b.maxY) / 200,
          w: (b.maxX - b.minX) / 100,
          d: (b.maxY - b.minY) / 100,
        },
      };
    }
    // Avec trémies : la dalle auto devient une forme découpée
    let multi = [[[[b.minX, b.minY], [b.maxX, b.minY], [b.maxX, b.maxY], [b.minX, b.maxY]]]][0];
    multi = [multi];
    try { multi = polygonClipping.difference(multi, ...stairCuts); } catch { /* ignore */ }
    return {
      geometries: multiToShapes(multi).map((shape) => new THREE.ExtrudeGeometry(shape, { depth: slabThickness / 100, bevelEnabled: false })),
    };
  }, [floor.walls, hasDrawnBaseSlab, stairCuts, slabThickness]);

  // Sols colorés des pièces (union des blocs → ShapeGeometry)
  const roomFloors = useMemo(() => {
    const out = [];
    for (const room of floor.rooms) {
      if ((room.kind ?? 'room') !== 'room' || !room.blocks?.length || room.hidden) continue;
      for (const { outer, holes } of roomRings(room)) {
        const shape = new THREE.Shape(outer.map((p) => new THREE.Vector2(p.x / 100, p.y / 100)));
        for (const h of holes) {
          shape.holes.push(new THREE.Path(h.map((p) => new THREE.Vector2(p.x / 100, p.y / 100))));
        }
        const color = `hsl(${roomHue(room.id)}, 35%, 42%)`;
        out.push({
          key: `${room.id}_${out.length}`,
          geometry: new THREE.ShapeGeometry(shape),
          material: surfaceMaterial(room.finish, textures, color, { side: THREE.DoubleSide }),
          base: baseOffsetOf(room),
        });
      }
    }
    return out;
  }, [floor, baseOffsetOf, textures]);

  useEffect(() => () => disposeMaterials(roomFloors.map((r) => r.material)), [roomFloors]);

  // Marches d'escalier (montent jusqu'au niveau supérieur, depuis leur dalle parente)
  const stairSteps = useMemo(() => {
    const out = [];
    for (const stair of floor.stairs ?? []) {
      if (stair.hidden) continue;
      const base = baseOffsetOf(stair);
      for (const b of stairToBoxes(stair, (rise ?? floor.height + slabThickness) - base)) {
        out.push({ ...b, z0: b.z0 + base });
      }
    }
    return out;
  }, [floor, rise, slabThickness, baseOffsetOf]);

  // Velux : panneau vitré au niveau du plafond
  const veluxes = (floor.openings ?? []).filter((o) => !o.wallId && o.position && !o.hidden);

  const elevM = elevation / 100;

  return (
    <group>
      {/* Sols des pièces (léger décalage au-dessus de leur dalle) */}
      {roomFloors.map((rf) => (
        <mesh key={rf.key} geometry={rf.geometry} material={rf.material} rotation-x={Math.PI / 2}
          position={[0, elevM + rf.base / 100 + 0.006, 0]} receiveShadow />
      ))}
      {/* Dalles dessinées (dalle basse / mezzanine) — extrusion vers le haut depuis leur base */}
      {drawnSlabs.map((s) => (
        <mesh
          key={s.key}
          geometry={s.geometry}
          rotation-x={Math.PI / 2}
          position={[0, elevM + (s.elevation + s.thickness) / 100, 0]}
          castShadow
          receiveShadow
        >
          <meshStandardMaterial color={s.color} side={THREE.DoubleSide} />
        </mesh>
      ))}
      {/* Dalle automatique sous les murs (découpée par les trémies si besoin) */}
      {autoSlab?.box && (
        <mesh position={[autoSlab.box.cx, elevM - slabThickness / 200, autoSlab.box.cz]} receiveShadow>
          <boxGeometry args={[autoSlab.box.w, slabThickness / 100, autoSlab.box.d]} />
          <meshStandardMaterial color={SLAB_COLOR} />
        </mesh>
      )}
      {autoSlab?.geometries?.map((geo, i) => (
        <mesh key={`as${i}`} geometry={geo} rotation-x={Math.PI / 2} position={[0, elevM, 0]} receiveShadow>
          <meshStandardMaterial color={SLAB_COLOR} side={THREE.DoubleSide} />
        </mesh>
      ))}
      {/* Toits */}
      {roofs.map((r) => (
        <mesh key={r.key} geometry={r.geometry} material={r.material} rotation-x={Math.PI / 2}
          position={[0, elevM + r.base / 100, 0]} castShadow receiveShadow />
      ))}
      {/* Marches d'escalier */}
      {stairSteps.map((s, i) => (
        <mesh
          key={`st${i}`}
          position={[s.x / 100, elevM + (s.z0 + s.h / 2) / 100, s.y / 100]}
          rotation={[0, -s.angle, 0]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[s.going / 100, s.h / 100, s.width / 100]} />
          <meshStandardMaterial color={STAIR_COLOR} />
        </mesh>
      ))}
      {/* Objets de la bibliothèque (électricité, plomberie, mobilier…) */}
      {(floor.items ?? []).filter((it) => !it.hidden).map((it) => (
        <ItemMesh key={it.id} item={it} catalog={itemCatalog} floorHeight={floor.height} elevM={elevM} />
      ))}
      {/* Charpente : poutres (droites ou rampantes) et poteaux */}
      {(floor.beams ?? []).filter((b) => !b.hidden).map((b) => {
        const box = beamBox(b, floor.height);
        if (box.kind === 'post') {
          return (
            <mesh
              key={b.id}
              position={[box.p.x / 100, elevM + (box.elevation + box.height / 2) / 100, box.p.y / 100]}
              material={beamMaterial(b.material)}
              castShadow
              receiveShadow
            >
              <boxGeometry args={[box.w / 100, box.height / 100, box.d / 100]} />
            </mesh>
          );
        }
        return (
          <group
            key={b.id}
            position={[box.mid.x / 100, elevM + (box.elev + box.h / 2) / 100, box.mid.y / 100]}
            rotation={[0, -box.angle, 0]}
          >
            <mesh rotation={[0, 0, box.tilt]} material={beamMaterial(b.material)} castShadow receiveShadow>
              <boxGeometry args={[box.len / 100, box.h / 100, box.w / 100]} />
            </mesh>
          </group>
        );
      })}
      {/* Velux au plafond */}
      {veluxes.map((v) => (
        <mesh
          key={v.id}
          position={[v.position.x / 100, elevM + floor.height / 100, v.position.y / 100]}
        >
          <boxGeometry args={[v.width / 100, 0.06, v.height / 100]} />
          <primitive object={glassMaterial()} attach="material" />
        </mesh>
      ))}
      {/* Murs (posés sur leur dalle parente le cas échéant), finitions int/ext par face */}
      {walls.map(({ wall, angle, boxes, base, exteriorSide }) => (
        <group
          key={wall.id}
          position={[wall.a.x / 100, elevM + base / 100, wall.a.y / 100]}
          rotation={[0, -angle, 0]}
        >
          {boxes.map((b, i) => (
            <mesh
              key={i}
              position={[(b.u + b.w / 2) / 100, (b.y0 + b.h / 2) / 100, 0]}
              material={b.mats}
              castShadow
              receiveShadow
            >
              <boxGeometry args={[b.w / 100, b.h / 100, wall.thickness / 100]} />
            </mesh>
          ))}
          {/* Menuiseries : dormants, vantaux, volets */}
          {floor.openings
            .filter((o) => o.wallId === wall.id && !o.hidden && o.type !== 'passage')
            .map((o) => (
              <OpeningMesh key={o.id} opening={o} wallThickness={wall.thickness}
                floorId={floor.id} exteriorSide={exteriorSide} />
            ))}
        </group>
      ))}
    </group>
  );
}
